import { Controller, useFormContext } from 'react-hook-form';
import {
  FormRichTextField,
  FormRichTextFieldProps,
} from './form-rich-text-field';
import { Grid, TextField, TextFieldProps } from '@mui/material';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { BaseSyntheticEvent } from 'react';
import { ConvergeInputLabel } from './converge-input-label';
import { FormFieldContainer } from './form-field-container';

export type FormTextFieldProps =
  | ({
      name: string;
      isPrimaryEndorsement?: boolean;
      containerWidth?: number;
      isRichText?: boolean;
      sideBySide?: boolean;
      hideMenu?: boolean;
      rows?: number;
      defaultValue?: string | null;
      autoComplete?: boolean;
      onBlur?: (e?: BaseSyntheticEvent<object, any, any> | undefined) => void;
      dataTestId?: string;
    } & Omit<TextFieldProps, 'autoComplete' | 'onBlur'>)
  | (Omit<FormRichTextFieldProps, 'autoComplete' | 'onBlur'> & {
      sideBySide?: boolean;
      autoComplete?: boolean;
      defaultValue?: string | null;
      isPrimaryEndorsement?: boolean;
      onBlur?: (e?: BaseSyntheticEvent<object, any, any> | undefined) => void;
      dataTestId?: string;
    });

export const FormTextField = ({
  name,
  label,
  required,
  containerWidth = 6,
  hideMenu,
  sideBySide,
  autoComplete,
  onBlur,
  defaultValue,
  isPrimaryEndorsement,
  value,
  rows = 10,
  dataTestId,
  ...otherProps
}: FormTextFieldProps) => {
  const { control } = useFormContext();

  if ('isRichText' in otherProps && otherProps.isRichText === true) {
    return (
      <FormRichTextField
        name={name}
        hideMenu={hideMenu}
        label={label}
        value={value}
        required={required}
        containerWidth={containerWidth}
        defaultValue={defaultValue}
        onBlur={() => onBlur && onBlur()}
        dataTestId={dataTestId}
        {...otherProps}
      />
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const labelComponent = otherProps.hiddenLabel !== true && (
          <ConvergeInputLabel label={label} required={required} />
        );

        const fieldComponent = (
          <TextField
            style={{ width: isPrimaryEndorsement ? '300px' : '' }}
            {...field}
            value={field.value ?? value}
            defaultValue={defaultValue}
            {...otherProps}
            inputProps={{
              defaultValue: defaultValue ?? undefined,
              autoComplete:
                typeof autoComplete == 'boolean'
                  ? autoComplete
                    ? 'on'
                    : 'off'
                  : undefined,
              ...(dataTestId && { 'data-testid': dataTestId }),
            }}
            variant="outlined"
            size="small"
            fullWidth
            error={!!error}
            onBlur={(e) => {
              field.onBlur();
              onBlur && onBlur(e);
            }}
            helperText={error ? (error?.message as unknown as string) : ''}
          />
        );

        return (
          <FormFieldContainer md={containerWidth}>
            {sideBySide ? (
              <Grid
                container
                flexDirection={'row'}
                flexGrow={1}
                flexBasis={1}
                direction={'row'}
                spacing={2}
              >
                <Grid item xs={6}>
                  {labelComponent}
                </Grid>
                <Grid item xs={6}>
                  {fieldComponent}
                </Grid>
              </Grid>
            ) : (
              <>
                {labelComponent}
                {fieldComponent}
              </>
            )}
          </FormFieldContainer>
        );
      }}
    />
  );
};
